import { useTranslation } from "next-i18next";
import { useState, useEffect } from "react";

import { useCurrentLocale } from "../context/LanguageContext";
import { LatLngLiteralProps } from "../interfaces/locations/location-interface";
import { getDefaultLocation } from "../shared/helpers/locations-helper";

type Settings = {
    enableHighAccuracy: boolean;
    timeout: number;
    maximumAge: number;
};

const defaultSettings: Settings = {
    enableHighAccuracy: false,
    timeout: Infinity,
    maximumAge: 0,
};

export const usePosition = (
    watch = false,
    settings: Settings = defaultSettings
): { zoom: number; lng: number; error: string; lat: number } => {
    const locale = useCurrentLocale();
    const { t } = useTranslation();
    const [position, setPosition] = useState<LatLngLiteralProps>(getDefaultLocation(locale));
    const [zoom, setZoom] = useState<number>(5);
    const [error, setError] = useState<string>(null);

    const onChange = ({ coords }) => {
        setPosition({ lat: coords.latitude, lng: coords.longitude });
        setZoom(12);
    };

    const onError = (error): void => {
        setError(error.message);
    };

    const setWatcher = (watch: boolean): number | void => {
        if (watch) {
            return navigator.geolocation.watchPosition(onChange, onError, settings);
        }
        return navigator.geolocation.getCurrentPosition(onChange, onError, settings);
    };

    useEffect(() => {
        if (navigator?.geolocation) {
            const watcher = setWatcher(watch);
            return () => watcher && navigator.geolocation.clearWatch(watcher);
        } else {
            setError(t("inStore.geoLocationNotSupported"));
        }
    }, [settings.enableHighAccuracy, settings.timeout, settings.maximumAge, t, setWatcher]);

    return { ...position, zoom, error };
};
